import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from "react";
import { themeContext } from "../../context/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { playSound, focusOnMouseMove } from "../../utilities/utilities";
import { useAuth } from "../../context/auth";
import { useGamepad } from "../../hooks/useGamepad";
import styles from "./SkinPreviewModal.module.css";

// Board-specific YouTube video URLs (only for premium boards)
const BOARD_VIDEO_URLS = {
  inferno: "https://youtu.be/6D2vy5F2VF4",
  frost: "https://youtu.be/2lswopA3iaA",
  spectrum: "https://youtu.be/nv_-VIRIIYQ",
  // 'none' board will not have a video URL - will show NO ASSETS
};

// Helper function to check if URL is a YouTube video
const isYouTubeUrl = (url) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

// Helper function to convert YouTube URL to embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;

  // Handle youtu.be format
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1].split('?')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }

  // Handle youtube.com/watch?v= format
  if (url.includes('youtube.com/watch?v=')) {
    const videoId = url.split('v=')[1].split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }

  // Handle youtube.com/embed/ format (already embedded)
  if (url.includes('youtube.com/embed/')) {
    return url;
  }

  return null;
};

// Helper function to get YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;

  // Handle youtu.be format
  if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1].split('?')[0];
  }

  // Handle youtube.com/watch?v= format
  if (url.includes('youtube.com/watch?v=')) {
    return url.split('v=')[1].split('&')[0];
  }

  // Handle youtube.com/embed/ format
  if (url.includes('youtube.com/embed/')) {
    return url.split('embed/')[1].split('?')[0];
  }

  return null;
};

// Helper function to get YouTube thumbnail URL
const getYouTubeThumbnailUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};


const PremiumBoardPreviewModal = ({ board, onClose, onSelect, game, isOwned, isPurchased = false, isTrial = false, user }) => {
  const { theme } = useContext(themeContext);
  const themeNumber = theme.replace("theme-", "");
  const { signIn } = useAuth();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(null); // 'left' or 'right'
  const [isSelecting, setIsSelecting] = useState(false);
  const [gamepadEnabled, setGamepadEnabled] = useState(false);
  const modalRef = useRef(null);

  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  // Enable gamepad with delay to prevent button that opened modal from closing it
  useEffect(() => {
    const timer = setTimeout(() => setGamepadEnabled(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Get board-specific video URL (only for premium boards, not for 'none')
  const boardVideoUrl = BOARD_VIDEO_URLS[board.id];
  const images = boardVideoUrl ? [boardVideoUrl] : [];
  const currentImage = images[currentImageIndex];
  const hasImages = images.length > 0;
  const isCurrentImageYouTube = isYouTubeUrl(currentImage);
  const youtubeEmbedUrl = isCurrentImageYouTube ? getYouTubeEmbedUrl(currentImage) : null;

  // Handle image navigation
  const goToNext = useCallback(() => {
    if (currentImageIndex < images.length - 1) {
      playSound(game, "navigation");
      setSlideDirection('left'); // Current image slides left, new comes from right
      setTimeout(() => {
        setCurrentImageIndex(prev => prev + 1);
      }, 50);
    }
  }, [currentImageIndex, images.length, game]);

  const goToPrevious = useCallback(() => {
    if (currentImageIndex > 0) {
      playSound(game, "navigation");
      setSlideDirection('right'); // Current image slides right, new comes from left
      setTimeout(() => {
        setCurrentImageIndex(prev => prev - 1);
      }, 50);
    }
  }, [currentImageIndex, game]);

  const goToImage = useCallback((index) => {
    if (index !== currentImageIndex && index >= 0 && index < images.length) {
      playSound(game, "navigation");
      // Determine direction based on index change
      setSlideDirection(index > currentImageIndex ? 'left' : 'right');
      setTimeout(() => {
        setCurrentImageIndex(index);
      }, 50);
    }
  }, [currentImageIndex, images.length, game]);


  // Handle close
  const handleClose = useCallback(() => {
    playSound(game, "click");
    onClose();
  }, [game, onClose]);

  // Handle select
  const handleSelect = useCallback(async () => {
    playSound(game, "click");
    if (onSelect) {
      setIsSelecting(true);
      try {
        await onSelect();
      } finally {
        setIsSelecting(false);
      }
    }
  }, [game, onSelect]);

  // Handle gamepad navigation
  const handleGamepadNavigation = useCallback((direction) => {
    if (!modalRef.current) return;
    const elements = Array.from(modalRef.current.querySelectorAll('[data-focusable="true"]'));
    if (elements.length === 0) return;

    focusableElements.elements = elements;
    if (focusableElements.index === undefined || focusableElements.index >= elements.length) {
      focusableElements.index = 0;
    }

    if (direction === 'left' || direction === 'up') {
      focusableElements.index = focusableElements.index <= 0 ? elements.length - 1 : focusableElements.index - 1;
    } else if (direction === 'right' || direction === 'down') {
      focusableElements.index = focusableElements.index >= elements.length - 1 ? 0 : focusableElements.index + 1;
    }

    if (elements[focusableElements.index]) {
      playSound(game, "navigation");
      elements[focusableElements.index].focus();
    }
  }, [focusableElements, game]);

  // Handle gamepad button presses
  const handleGamepadButtonPress = useCallback((button) => {
    if (button === 'X' || button === 'A') {
      if (!modalRef.current) return;
      const elements = Array.from(modalRef.current.querySelectorAll('[data-focusable="true"]'));
      if (focusableElements.index < elements.length && elements[focusableElements.index]) {
        playSound(game, "click");
        elements[focusableElements.index].click();
      }
    } else if (button === 'B' || button === 'Start') {
      handleClose();
    }
  }, [focusableElements, handleClose, game]);

  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: gamepadEnabled,
    gamepadIndex: null,
  });

  // Sync focusable elements when content changes
  useEffect(() => {
    if (modalRef.current) {
      const elements = Array.from(modalRef.current.querySelectorAll('[data-focusable="true"]'));
      focusableElements.elements = elements;
      if (focusableElements.index >= elements.length) {
        focusableElements.index = Math.max(0, elements.length - 1);
      }
      if (elements.length > 0 && elements[focusableElements.index]) {
        elements[focusableElements.index].focus();
      }
    }
  }, [isOwned, focusableElements]);

  // Auto-focus first element on mount
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
      const elements = modalRef.current.querySelectorAll('[data-focusable="true"]');
      focusableElements.elements = Array.from(elements);
      focusableElements.index = 0;
      if (elements.length > 0) {
        elements[0].focus();
      }
    }
  }, []);

  // Reset slide direction after animation completes
  useEffect(() => {
    if (slideDirection) {
      const timer = setTimeout(() => {
        setSlideDirection(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [slideDirection]);

  return (
    <div
      className={styles.overlay}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`${styles.modal} ${styles["modal-theme-" + themeNumber]}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
        onKeyDown={(event) => {
          const modal = event.currentTarget;
          const elements = Array.from(modal.querySelectorAll('[data-focusable="true"]'));
          if (elements.length === 0 && event.key !== 'Escape') return;

          focusableElements.elements = elements;
          if (focusableElements.index === undefined || focusableElements.index >= elements.length) {
            focusableElements.index = 0;
          }

          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            focusableElements.index = focusableElements.index <= 0 ? elements.length - 1 : focusableElements.index - 1;
            if (elements[focusableElements.index]) {
              playSound(game, "navigation");
              elements[focusableElements.index].focus();
            }
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            focusableElements.index = focusableElements.index >= elements.length - 1 ? 0 : focusableElements.index + 1;
            if (elements[focusableElements.index]) {
              playSound(game, "navigation");
              elements[focusableElements.index].focus();
            }
          } else if (event.key === 'Enter') {
            event.preventDefault();
            if (elements[focusableElements.index]) {
              playSound(game, "click");
              elements[focusableElements.index].click();
            }
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            handleClose();
          }
        }}
        onMouseMove={(event) => {
          const modal = event.currentTarget;
          const elements = Array.from(modal.querySelectorAll('[data-focusable="true"]'));
          if (elements.length > 0) {
            focusableElements.elements = elements;
            focusOnMouseMove(event, focusableElements, game, playSound);
          }
        }}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{board.name}</h2>
            {images.length > 1 && (
              <span className={styles.imageCounter}>
                {currentImageIndex + 1} / {images.length}
              </span>
            )}
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.iconButton}
              onClick={handleClose}
              aria-label="Close preview"
              title="Close (Esc)"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Thumbnail Strip - only show if multiple images exist */}
        {images.length > 1 && (
          <div className={styles.thumbnailContainer}>
            <div className={styles.thumbnailStrip}>
              {images.map((img, index) => {
                const isYouTube = isYouTubeUrl(img);
                const thumbnailSrc = isYouTube ? getYouTubeThumbnailUrl(img) : img;

                return (
                  <button
                    key={index}
                    className={`${styles.thumbnail} ${
                      index === currentImageIndex ? styles.thumbnailActive : ""
                    }`}
                    onClick={() => goToImage(index)}
                    aria-label={`View ${isYouTube ? 'video' : 'image'} ${index + 1}`}
                  >
                    <img src={thumbnailSrc} alt={`Thumbnail ${index + 1}`} />
                    {index === currentImageIndex && (
                      <div className={styles.thumbnailOverlay}></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Image Display */}
        <div className={styles.imageContainer}>
          {/* Navigation Arrows - only show if multiple images exist */}
          {images.length > 1 && currentImageIndex > 0 && (
            <button
              className={`${styles.navArrow} ${styles.navArrowLeft}`}
              onClick={goToPrevious}
              aria-label="Previous image"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}

          {/* Image or Video */}
          <div className={styles.imageWrapper}>
            {!hasImages ? (
              <div className={styles.noAssets}>
                <p>NO ASSETS</p>
              </div>
            ) : (
              <>
                {isCurrentImageYouTube && youtubeEmbedUrl ? (
                  <iframe
                    src={youtubeEmbedUrl}
                    title={`${board.name} preview video ${currentImageIndex + 1}`}
                    className={`${styles.mainImage} ${styles.videoEmbed} ${
                      slideDirection === 'left' ? styles.slideInFromRight :
                      slideDirection === 'right' ? styles.slideInFromLeft : ''
                    }`}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <img
                    src={currentImage}
                    alt={`${board.name} preview ${currentImageIndex + 1}`}
                    className={`${styles.mainImage} ${
                      slideDirection === 'left' ? styles.slideInFromRight :
                      slideDirection === 'right' ? styles.slideInFromLeft : ''
                    }`}
                  />
                )}
              </>
            )}
          </div>

          {images.length > 1 && currentImageIndex < images.length - 1 && (
            <button
              className={`${styles.navArrow} ${styles.navArrowRight}`}
              onClick={goToNext}
              aria-label="Next image"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}
        </div>

        {/* Description */}
        {board.description && (
          <p className={styles.description}>{board.description}</p>
        )}

        {/* Bundle Info - only show for paid boards not purchased */}
        {!isPurchased && !board.isFree && (
          <div className={styles.bundleInfo}>
            <p className={styles.bundleText}>
              Includes {board.name} board + {board.name} app skin
            </p>
          </div>
        )}

        {/* Footer with Select and/or Buy Button */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {user && (isOwned || board.isFree) && (
              <button
                className={styles.purchaseButton}
                onClick={handleSelect}
                disabled={isSelecting}
                data-focusable="true"
              >
                <span>SELECT</span>
              </button>
            )}

            {!isPurchased && !board.isFree && (
              <button
                className={styles.purchaseButton}
                onClick={() => {
                  if (board.buyUrl) {
                    playSound(game, "click");

                    // Add email query parameter if user is logged in
                    let purchaseUrl = board.buyUrl;
                    if (user && user.email) {
                      const urlObj = new URL(board.buyUrl);
                      urlObj.searchParams.set("email", user.email);
                      purchaseUrl = urlObj.toString();
                    }

                    window.open(purchaseUrl, "_blank");
                    onClose();
                  }
                }}
                data-focusable="true"
              >
                <span>Buy — $5</span>
              </button>
            )}

            {!user && isTrial && !board.isFree && (
              <button
                className={styles.tryButton}
                onClick={() => { playSound(game, "click"); signIn(); }}
                data-focusable="true"
              >
                <span>Try for Free</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PremiumBoardPreviewModal;
